import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

interface ReaderActionTarget {
  id: string;
  url: string;
  title?: string | null;
}

interface ReaderActionOpts {
  /** Called after a terminal action (archive / file / delete) succeeds. */
  onDone?: () => void;
  /** Called synchronously before a delete fires — e.g. to stop scroll-progress
   *  saves on an article that's about to disappear. */
  onBeforeDelete?: () => void;
}

/**
 * The four reader exits, shared by the full-page reader and the Queue
 * split-pane panel so the action set and contracts can't drift:
 *
 * - Archive  → status `archived` (kept in Grove's archive)
 * - File to… → status `filed` + chosen vaultPath (graduates into the vault)
 * - Delete   → soft-delete (recoverable)
 * - Share    → opens the share dialog (public Grove link, email, social).
 *              NOTE: non-terminal — it does not call `onDone`/`onBeforeDelete`,
 *              unlike the other three. Callers render <ShareDialog> with the
 *              returned `shareOpen` / `closeShare` pair.
 */
export function useReaderActions(article: ReaderActionTarget, opts: ReaderActionOpts = {}) {
  const queryClient = useQueryClient();
  const { onDone, onBeforeDelete } = opts;

  // Prefix-matches every ['articles', ...] query (queue, library, detail).
  const finish = () => {
    queryClient.invalidateQueries({ queryKey: ['articles'] });
    onDone?.();
  };

  const patch = useMutation({
    mutationFn: (body: Parameters<typeof api.updateArticle>[1]) =>
      api.updateArticle(article.id, body),
    onSuccess: finish,
  });

  const del = useMutation({
    mutationFn: () => api.deleteArticle(article.id),
    onSuccess: finish,
  });

  const archive = () => patch.mutate({ status: 'archived' });

  const fileTo = () => {
    const path = window.prompt('File to vault folder (e.g. Research/AI):');
    if (!path?.trim()) return;
    patch.mutate({ status: 'filed', vaultPath: path.trim() });
  };

  const remove = () => {
    if (!window.confirm('Delete this article?')) return;
    onBeforeDelete?.();
    del.mutate();
  };

  // Sharing goes through the dialog rather than straight to the OS sheet: it
  // has to mint the public Grove link first, and offers email + social from
  // the same place.
  const [shareOpen, setShareOpen] = useState(false);

  return {
    archive,
    fileTo,
    remove,
    share: () => setShareOpen(true),
    shareOpen,
    closeShare: () => setShareOpen(false),
    isPending: patch.isPending || del.isPending,
  };
}
